<template>
  <div style="width: 200px; margin-top: 10px;">
    <q-inner-loading
      :showing="true"
      style="background: transparent; z-index: 0;"
    />
    <div
      ref="monitorContainer"
      style="transform: scale(-1, 1) scaleX(-0.5) scaleY(0.5) translate(-100px, -100px); height: 200px; width: 200px;"
    />
    <div class="row justify-between items-center q-mt-sm">
      <q-btn
        v-show="videoDevices.length > 1"
        class="col-auto"
        icon="cached"
        size="md"
        dense
        outline
        @click="videoDeviceIndex += 1"
      />
      <q-btn
        class="col-auto"
        icon="stop"
        size="md"
        color="red"
        dense
        outline
        @click="stop"
      />
      <q-toggle
        :model-value="continuous"
        keep-color
        class="col-auto"
        label="Continuous"
        :color="continuous ? 'green' : 'red'"
        unchecked-icon="clear"
        checked-icon="check"
        @click.stop
        @update:model-value="toggleContinuous"
      />
    </div>
    <video
      ref="videoSource"
      class="fill-width"
      style="display: none;"
      autoplay
      playsinline
      muted
    />
  </div>
</template>

<script>

import QrScanner from 'qr-scanner'

export default {
  name: 'QrCodeScanner',
  props: {
    visible: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['running', 'done'],
  data () {
    return {
      videoDevices: [],
      videoDeviceIndex: 0,
      scanner: null,
      continuous: false
    }
  },
  watch: {
    videoDeviceIndex () {
      this.setCamera()
    },
    scanner () {
      this.$emit('running', !!this.scanner)
    },
    visible () {
      if (this.visible && !this.scanner) {
        this.setupScanner()
      }
    }
  },
  unmounted () {
    this.teardown()
  },
  mounted () {
    this.initialize()
  },
  methods: {
    async initialize () {
      this.continuous = localStorage.continuousQrScanning === 'true'
      this.videoDeviceIndex = +localStorage.qrCodeVideoDeviceIndex || 0

      if (this.continuous) {
        this.setupScanner()
        this.setCamera()
      }
    },
    toggleContinuous () {
      this.continuous = !this.continuous
      localStorage.continuousQrScanning = this.continuous

      if (this.continuous && this.singleScanTimer) {
        clearTimeout(this.singleScanTimer)
      }
    },
    setupDeviceListMonitoring () {
      this.setupDeviceList()
      this.deviceEventListener = (event) => {
        this.setupDeviceList()
      }
      navigator.mediaDevices.addEventListener('devicechange', this.deviceEventListener)
    },
    async setupDeviceList () {
      QrScanner.listCameras().then(cameras => {
        this.videoDevices = cameras
        this.setCamera()
      }).catch(e => console.log(e))
    },
    stop () {
      if (this.continuous) {
        this.toggleContinuous()
      }
      this.teardown()
      this.$emit('done')
    },
    async setupScanner () {
      if (this.scanner) return

      // Need to wait until monitor is mounted on dom
      await this.$nextTick()

      const videoSource = this.$refs.videoSource
      try {
        this.scanner = new QrScanner(videoSource, result => {
          const inCoolDown = !!this.debouceTimer

          clearTimeout(this.debouceTimer)
          this.debouceTimer = setTimeout(() => {
            this.debouceTimer = null
          }, 500)

          if (inCoolDown) return

          this.successfulRead(result)
        })
        this.scanner.start().then(() => {
          this.setupDeviceListMonitoring()
          this.$refs.monitorContainer.appendChild(this.scanner.$canvas)
          if (!this.continuous) {
            // For a single scan, turn off the camera if we haven't read after some time
            clearTimeout(this.singleScanTimer)
            this.singleScanTimer = setTimeout(() => {
              if (this.continuous) return

              this.teardown()
            }, 30000)
          }
        }).catch(e => console.log(e))
      } catch (err) {
        console.error(err)
      }
    },
    setCamera () {
      if (!this.videoDevices?.length) return

      const deviceIndex = this.videoDeviceIndex % this.videoDevices.length
      localStorage.qrCodeVideoDeviceIndex = deviceIndex

      const device = this.videoDevices[deviceIndex]

      this.scanner.setCamera(device.id)
    },
    successfulRead (value) {
      clearTimeout(this.singleScanTimer)
      this.$cardHandler.handleCardData(value)
      this.$piercingBeep()
      if (!this.continuous) {
        this.$emit('done')
      }

      if (this.continuous) return
      this.teardown()
    },
    teardown () {
      if (!this.scanner) return

      // Stop looking for new devices to be added
      if (navigator?.mediaDevices?.removeEventListener) navigator.mediaDevices.removeEventListener('devicechange', this.deviceEventListener)

      // Close the video stream
      this.scanner.stop()
      this.scanner = null

      // Clear monitor
      if (this.$refs.monitorContainer) {
        this.$refs.monitorContainer.innerHTML = ''
      }

      // Hide in case we were viewing
      this.$emit('done')
    }
  }
}

</script>
